import * as THREE from "https://cdn.jsdelivr.net/npm/three@0.160.0/build/three.module.min.js";

/* ── Setup ─────────────────────────────────────────────────────────────── */
const scroller = document.getElementById("uw-scroll");
const canvas = document.getElementById("uw-scene");
const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false });
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.setSize(innerWidth, innerHeight);
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.1;

const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(46, innerWidth / innerHeight, 0.1, 200);
camera.position.set(0, 0, 10);

/* ── Sky phases (scroll 0→1: dawn → golden → sunset → dusk → night) ────── */
const PHASES = [
  // [p, topColor, midColor, botColor, sunY, sunGlow, starA, cloudA, fog]
  [0.00, 0x2b1655, 0xb3407a, 0xff8c5a, -0.05, 1.05, 0.00, 0.85, 0x50354a],
  [0.22, 0x3d1d4e, 0xd4544f, 0xffb340, -0.35, 1.25, 0.00, 0.70, 0x5c3a3a],
  [0.45, 0x2a1060, 0xc0308a, 0xff6b6b, -0.80, 0.95, 0.10, 0.55, 0x4a2c48],
  [0.70, 0x140f45, 0x4a2a9e, 0x8a4ab8, -1.60, 0.40, 0.55, 0.28, 0x2a2044],
  [1.00, 0x04051a, 0x14164a, 0x2a2a7a, -2.60, 0.00, 1.00, 0.06, 0x0c0e22],
];
const cA = new THREE.Color(), cB = new THREE.Color();
function lerpHex(a, b, t, target) { cA.setHex(a); cB.setHex(b); return target.copy(cA).lerp(cB, t); }
function samplePhase(p) {
  for (let i = 0; i < PHASES.length - 1; i++) {
    const a = PHASES[i], b = PHASES[i + 1];
    if (p >= a[0] && p <= b[0]) {
      const t0 = (p - a[0]) / (b[0] - a[0]);
      const t = t0 * t0 * (3 - 2 * t0);
      return { a, b, t };
    }
  }
  return { a: PHASES.at(-1), b: PHASES.at(-1), t: 0 };
}

/* ── Sky dome (shader gradient, scroll-blended) ────────────────────────── */
const skyUniforms = {
  uTop: { value: new THREE.Color(PHASES[0][1]) },
  uMid: { value: new THREE.Color(PHASES[0][2]) },
  uBot: { value: new THREE.Color(PHASES[0][3]) },
  uTime: { value: 0 },
};
const sky = new THREE.Mesh(
  new THREE.PlaneGeometry(2, 2),
  new THREE.ShaderMaterial({
    depthWrite: false, depthTest: false,
    uniforms: skyUniforms,
    vertexShader: `varying vec2 vUv; void main(){ vUv=uv; gl_Position=vec4(position.xy,1.,1.); }`,
    fragmentShader: `
      varying vec2 vUv; uniform vec3 uTop,uMid,uBot; uniform float uTime;
      float hash(vec2 p){ return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453); }
      void main(){
        float y = vUv.y;
        vec3 col = mix(uBot, uMid, smoothstep(0.0,0.55,y));
        col = mix(col, uTop, smoothstep(0.45,1.0,y));
        col += 0.012*sin(y*80.0 + uTime*0.15) * (1.0-y);
        col += (hash(vUv*vec2(1920.,1080.))-.5)*0.012;
        gl_FragColor = vec4(col,1.);
      }`,
  })
);
sky.renderOrder = -1;
sky.frustumCulled = false;
scene.add(sky);

/* ── Soft sprite texture helper ────────────────────────────────────────── */
function radialSprite(inner, outer, stops) {
  const c = document.createElement("canvas");
  c.width = c.height = 128;
  const g = c.getContext("2d");
  const rg = g.createRadialGradient(64, 64, inner, 64, 64, outer);
  stops.forEach(([o, col]) => rg.addColorStop(o, col));
  g.fillStyle = rg;
  g.fillRect(0, 0, 128, 128);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/* ── The Sun (sets as you rise) ────────────────────────────────────────── */
const sunTex = radialSprite(0, 64, [
  [0, "rgba(255,250,235,1)"], [0.14, "rgba(255,220,140,1)"],
  [0.38, "rgba(255,140,70,0.55)"], [0.7, "rgba(255,80,120,0.18)"], [1, "rgba(255,80,120,0)"],
]);
const sun = new THREE.Sprite(new THREE.SpriteMaterial({
  map: sunTex, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
}));
sun.scale.setScalar(14);
sun.position.set(1.6, -1, -30);
scene.add(sun);

/* ── The Moon (rises at night) ─────────────────────────────────────────── */
const moonTex = radialSprite(0, 64, [
  [0, "rgba(235,240,255,1)"], [0.2, "rgba(210,220,255,0.9)"],
  [0.4, "rgba(170,185,255,0.25)"], [1, "rgba(150,170,255,0)"],
]);
const moon = new THREE.Sprite(new THREE.SpriteMaterial({
  map: moonTex, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, opacity: 0,
}));
moon.scale.setScalar(5);
moon.position.set(-4.5, -8, -26);
scene.add(moon);

/* ── Clouds (billboard planes, procedural puffs) ───────────────────────── */
function cloudTexture(seed) {
  const c = document.createElement("canvas");
  c.width = 256; c.height = 128;
  const g = c.getContext("2d");
  let s = seed;
  const rnd = () => (s = (s * 16807) % 2147483647) / 2147483647;
  for (let i = 0; i < 34; i++) {
    const x = 40 + rnd() * 176, y = 50 + rnd() * 36, r = 18 + rnd() * 38;
    const rg = g.createRadialGradient(x, y, 0, x, y, r);
    rg.addColorStop(0, "rgba(255,230,220,0.07)");
    rg.addColorStop(0.6, "rgba(255,220,215,0.03)");
    rg.addColorStop(1, "rgba(255,220,215,0)");
    g.fillStyle = rg;
    g.fillRect(0, 0, 256, 128);
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}
const clouds = new THREE.Group();
const cloudMats = [];
for (let i = 0; i < 9; i++) {
  const mat = new THREE.MeshBasicMaterial({
    map: cloudTexture(37 + i * 101), transparent: true, depthWrite: false,
    blending: THREE.AdditiveBlending, opacity: 0.5,
  });
  cloudMats.push(mat);
  const w = 9 + Math.random() * 12;
  const m = new THREE.Mesh(new THREE.PlaneGeometry(w, w * 0.45), mat);
  m.position.set((Math.random() - 0.5) * 34, -3 + i * 3.4 + Math.random() * 2, -16 - Math.random() * 14);
  m.userData.drift = 0.08 + Math.random() * 0.14;
  clouds.add(m);
}
scene.add(clouds);

/* ── Stars (fade in at night) + magic motes ────────────────────────────── */
const dotTex = radialSprite(0, 32, [
  [0, "rgba(255,255,255,1)"], [0.35, "rgba(255,230,210,0.5)"], [1, "rgba(255,230,210,0)"],
]);
function makePoints(count, spread, size, color, opacity, palette) {
  const geo = new THREE.BufferGeometry();
  const pos = new Float32Array(count * 3);
  const col = palette ? new Float32Array(count * 3) : null;
  const pc = new THREE.Color();
  for (let i = 0; i < count; i++) {
    pos[i * 3] = (Math.random() - 0.5) * spread;
    pos[i * 3 + 1] = (Math.random() - 0.5) * spread * 1.7;
    pos[i * 3 + 2] = -6 - Math.random() * 30;
    if (col) {
      pc.setHex(palette[i % palette.length]);
      col[i * 3] = pc.r; col[i * 3 + 1] = pc.g; col[i * 3 + 2] = pc.b;
    }
  }
  geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  if (col) geo.setAttribute("color", new THREE.BufferAttribute(col, 3));
  const mat = new THREE.PointsMaterial({
    size, map: dotTex, transparent: true, depthWrite: false,
    blending: THREE.AdditiveBlending, color, opacity, vertexColors: !!palette,
  });
  return new THREE.Points(geo, mat);
}
const stars = makePoints(1600, 46, 0.14, 0xdfe6ff, 0);
stars.position.y = 14;
scene.add(stars);
// iridescent "everyday magic" motes — coral / pink / violet / mint
const motes = makePoints(600, 20, 0.11, 0xffffff, 0.6, [0xffb27a, 0xff7ab8, 0xa78bfa, 0x5eead4]);
scene.add(motes);

/* ── Aurora ribbons (night finale) ─────────────────────────────────────── */
const auroraGroup = new THREE.Group();
const auroraMats = [];
function makeAurora(color, y0, tilt, speed, seed) {
  const mat = new THREE.ShaderMaterial({
    transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
    uniforms: {
      uTime: { value: 0 }, uAlpha: { value: 0 },
      uColor: { value: new THREE.Color(color) },
      uSpeed: { value: speed }, uSeed: { value: seed },
    },
    vertexShader: `varying vec2 vUv; void main(){ vUv=uv; gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.); }`,
    fragmentShader: `
      varying vec2 vUv; uniform float uTime,uAlpha,uSpeed,uSeed; uniform vec3 uColor;
      void main(){
        float wave = sin(vUv.x*7.0 + uTime*uSpeed + uSeed)*0.16
                   + sin(vUv.x*13.0 - uTime*uSpeed*0.7 + uSeed*2.0)*0.07;
        float d = abs(vUv.y - 0.5 - wave);
        float band = smoothstep(0.30, 0.02, d);
        float ends = smoothstep(0.0,.12,vUv.x)*smoothstep(1.,.88,vUv.x);
        float shimmer = .8 + .2*sin(uTime*1.7 + vUv.x*20.0 + uSeed);
        gl_FragColor = vec4(uColor, band*ends*shimmer*.5*uAlpha);
      }`,
  });
  auroraMats.push(mat);
  const m = new THREE.Mesh(new THREE.PlaneGeometry(34, 7), mat);
  m.position.set(0, y0, -24);
  m.rotation.z = tilt;
  return m;
}
auroraGroup.add(makeAurora(0x5eead4, 5.2, 0.10, 0.35, 1.7));
auroraGroup.add(makeAurora(0x9a6bff, 3.4, -0.06, 0.28, 4.2));
auroraGroup.add(makeAurora(0xff7ac8, 6.8, 0.05, 0.22, 7.9));
scene.add(auroraGroup);

/* ── Shooting star ─────────────────────────────────────────────────────── */
const streak = new THREE.Mesh(
  new THREE.PlaneGeometry(3.2, 0.03),
  new THREE.MeshBasicMaterial({ color: 0xeef2ff, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending })
);
streak.position.set(0, 0, -20);
streak.rotation.z = -0.5;
scene.add(streak);
let streakT = -3;

/* ── Scroll + mouse (polled in tick — no event dependence) ─────────────── */
let scrollTarget = 0, scrollEased = 0;
const mouse = { x: 0, y: 0, ex: 0, ey: 0 };
function pollScroll() {
  const max = scroller.scrollHeight - scroller.clientHeight;
  scrollTarget = max > 0 ? Math.min(1, Math.max(0, scroller.scrollTop / max)) : 0;
}
addEventListener("mousemove", (e) => {
  mouse.x = (e.clientX / innerWidth - 0.5) * 2;
  mouse.y = (e.clientY / innerHeight - 0.5) * 2;
}, { passive: true });

/* ── DOM: loader, reveals, progress, ticker ────────────────────────────── */
const loader = document.getElementById("uw-loader");
const pctEl = loader.querySelector(".uw-loader-pct");
const barEl = loader.querySelector(".uw-loader-line span");
let fakePct = 0;
const loadTimer = setInterval(() => {
  fakePct = Math.min(100, fakePct + Math.random() * 16);
  pctEl.textContent = Math.floor(fakePct);
  barEl.style.width = fakePct + "%";
  if (fakePct >= 100) {
    clearInterval(loadTimer);
    loader.classList.add("done");
    document.querySelectorAll(".uw-hero .uw-reveal").forEach((el, i) =>
      setTimeout(() => el.classList.add("uw-in"), 150 + i * 160));
  }
}, 90);

const pending = new Set(document.querySelectorAll(".uw-panel:not(.uw-hero) .uw-reveal"));
function revealEl(el) {
  const sibs = [...el.parentElement.querySelectorAll(".uw-reveal")];
  const idx = sibs.indexOf(el);
  setTimeout(() => el.classList.add("uw-in"), (idx % 6) * 110);
  pending.delete(el);
}
const io = new IntersectionObserver((entries) => {
  entries.forEach((en) => {
    if (en.isIntersecting) { revealEl(en.target); io.unobserve(en.target); }
  });
}, { root: scroller, threshold: 0.25 });
pending.forEach((el) => io.observe(el));
// fallback sweep: some browsers under-deliver IO callbacks in embedded contexts
let sweepFrame = 0;
function revealSweep() {
  if (!pending.size || ++sweepFrame % 20) return;
  const vh = innerHeight;
  pending.forEach((el) => {
    const r = el.getBoundingClientRect();
    if (r.top < vh * 0.85 && r.bottom > 0) { io.unobserve(el); revealEl(el); }
  });
}

const ticker = document.getElementById("uw-ticker");
ticker.innerHTML += ticker.innerHTML;

const progressBar = document.getElementById("uw-progressBar");

/* ── Resize (event + per-frame poll) ───────────────────────────────────── */
function syncSize() {
  const w = innerWidth, h = innerHeight;
  const size = renderer.getSize(new THREE.Vector2());
  if (size.x !== w || size.y !== h) {
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
  }
}
addEventListener("resize", syncSize);

/* ── Loop ──────────────────────────────────────────────────────────────── */
const clock = new THREE.Clock();
const tmpTop = new THREE.Color(), tmpMid = new THREE.Color(), tmpBot = new THREE.Color();
let lastFrame = 0;
function renderFrame() {
  lastFrame = performance.now();
  const t = clock.getElapsedTime();
  pollScroll();
  syncSize();
  revealSweep();
  scrollEased += (scrollTarget - scrollEased) * 0.06;
  mouse.ex += (mouse.x - mouse.ex) * 0.05;
  mouse.ey += (mouse.y - mouse.ey) * 0.05;
  const p = scrollEased;

  // blend sky phase
  const { a, b, t: pt } = samplePhase(p);
  skyUniforms.uTop.value.copy(lerpHex(a[1], b[1], pt, tmpTop));
  skyUniforms.uMid.value.copy(lerpHex(a[2], b[2], pt, tmpMid));
  skyUniforms.uBot.value.copy(lerpHex(a[3], b[3], pt, tmpBot));
  skyUniforms.uTime.value = t;

  const sunY = a[4] + (b[4] - a[4]) * pt;
  const sunGlow = a[5] + (b[5] - a[5]) * pt;
  const starA = a[6] + (b[6] - a[6]) * pt;
  const cloudA = a[7] + (b[7] - a[7]) * pt;

  // camera rises through the sky
  const rise = p * 14;
  camera.position.y = rise + mouse.ey * -0.35;
  camera.position.x = mouse.ex * 0.55;
  camera.lookAt(mouse.ex * 0.3, rise + 0.6, -18);

  // sun sinks below as you ascend
  sun.position.y = sunY * 6 + Math.sin(t * 0.4) * 0.15;
  sun.material.opacity = Math.max(0, Math.min(1, sunGlow));
  sun.scale.setScalar(14 + Math.sin(t * 0.8) * 0.4);

  // moon rises near the top
  const moonIn = Math.max(0, (p - 0.62) / 0.38);
  moon.material.opacity = moonIn * 0.95;
  moon.position.y = rise + 3.2 - (1 - moonIn) * 5;
  moon.position.x = -4.5 + Math.sin(t * 0.15) * 0.3;

  // clouds drift; fade toward night
  clouds.children.forEach((m, i) => {
    m.position.x += Math.sin(t * 0.1 + i) * 0.0015 + m.userData.drift * 0.004;
    if (m.position.x > 20) m.position.x = -20;
  });
  cloudMats.forEach((m) => { m.opacity = cloudA * 0.55; });

  // stars twinkle in
  stars.material.opacity = starA;
  stars.rotation.z = t * 0.004;
  stars.position.y = rise * 0.55 + 8;
  // aurora dances in with the stars
  auroraGroup.position.y = rise;
  auroraMats.forEach((m) => { m.uniforms.uTime.value = t; m.uniforms.uAlpha.value = starA * starA; });
  motes.material.opacity = 0.5 - p * 0.25;
  motes.rotation.y = t * 0.015;
  motes.position.y = rise * 0.85;

  // occasional shooting star once the sky is dark
  if (starA > 0.5) {
    streakT += 0.016;
    if (streakT > 0) {
      const life = streakT / 1.1;
      if (life >= 1) { streakT = -(2.5 + Math.random() * 4); streak.material.opacity = 0; }
      else {
        streak.material.opacity = Math.sin(life * Math.PI) * 0.9;
        streak.position.set(-8 + life * 15, rise + 5.5 - life * 3.2, -20);
      }
    }
  } else { streak.material.opacity = 0; }

  progressBar.style.width = p * 100 + "%";
  renderer.render(scene, camera);
}
function tick() {
  renderFrame();
  requestAnimationFrame(tick);
}
tick();
// watchdog: keep the scene alive if requestAnimationFrame stalls
setInterval(() => {
  if (performance.now() - lastFrame > 300) renderFrame();
}, 250);

/* ── Card tilt ─────────────────────────────────────────────────────────── */
document.querySelectorAll(".uw-card").forEach((card) => {
  card.addEventListener("mousemove", (e) => {
    const r = card.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    card.style.transform = `perspective(900px) rotateY(${x * 10}deg) rotateX(${-y * 10}deg) translateZ(6px)`;
  });
  card.addEventListener("mouseleave", () => { card.style.transform = ""; });
});
